'use client'

import { useEffect, useState } from 'react'

import Swal from 'sweetalert2'

import { env } from '@/env'
import { useAuthContext } from '@/features/auth/contexts/AuthContext'
import { showErrorAlert } from '@/shared/lib/ui'

export function ConfirmationForm() {
  const {
    isLoading,
    setIsLoading,
    httpClient,
    currentEmail,
    goToSignIn,
    handleSignIn,
    currentPassword,
    callbackUrl
  } = useAuthContext()

  const [token, setToken] = useState('')
  const [secondsLeft, setSecondsLeft] = useState(30)

  useEffect(() => {
    if (secondsLeft <= 0) return
    const timer = window.setTimeout(() => setSecondsLeft(value => value - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [secondsLeft])

  const handleValidateToken = async () => {
    if (isLoading || token.length < 6 || !currentEmail) return

    setIsLoading(true)
    try {
      await httpClient.post('/auth/confirmation', {
        email: currentEmail,
        token
      })

      await Swal.fire({
        icon: 'success',
        title: 'Conta confirmada com sucesso'
      })

      if (currentPassword) {
        const { ok } = await handleSignIn({
          login: currentEmail,
          password: currentPassword
        })

        if (ok) {
          window.location.replace(callbackUrl)
          return
        }
      }

      goToSignIn()
    } catch (error) {
      await showErrorAlert(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendToken = async () => {
    if (isLoading || !currentEmail) return
    setIsLoading(true)

    try {
      await httpClient.post('/auth/request-confirmation-token', {
        email: currentEmail
      })
      setSecondsLeft(30)
      await Swal.fire({
        icon: 'success',
        title: 'Novo código enviado com sucesso'
      })
    } catch (error) {
      await showErrorAlert(error)
    } finally {
      setIsLoading(false)
    }
  }

  const hiddenEmail = currentEmail
    ? (() => {
        const [name, domain] = currentEmail.split('@')
        if (env.NEXT_PUBLIC_PGEE_APP_ENV === 'development') return currentEmail
        return `${name[0]}${'*'.repeat(Math.max(name.length - 1, 0))}@${domain}`
      })()
    : ''

  return (
    <div className="flex flex-col items-center justify-center px-6 pb-8 lg:px-10">
      <div className="mb-8 text-center">
        <h1 className="mb-2 text-2xl font-bold text-foreground dark:text-white">
          Etapa de confirmação
        </h1>
        <p className="mb-2 text-base text-gray-800 dark:text-gray-100">
          Digite o código de confirmação enviado para
        </p>
        <p className="text-xl font-semibold text-foreground dark:text-white">
          {hiddenEmail}
        </p>
      </div>

      <div className="mb-8 grid w-full max-w-xs gap-2">
        <label className="text-left text-sm font-medium text-foreground dark:text-white">
          Digite seu código de 6 dígitos
        </label>
        <input
          value={token}
          onChange={event =>
            setToken(event.target.value.replace(/\D/g, '').slice(0, 6))
          }
          className="w-full rounded-lg border px-3 py-2 text-center text-lg tracking-[0.5em]"
          inputMode="numeric"
          maxLength={6}
        />
        <button
          type="button"
          className="rounded-lg bg-primary px-4 py-2 text-white disabled:opacity-50"
          disabled={token.length < 6 || isLoading}
          onClick={handleValidateToken}
        >
          Confirmar
        </button>
      </div>

      <div className="text-center text-base font-medium text-gray-800 dark:text-gray-100">
        <span className="mr-1">Não recebeu o código? Peça um novo</span>
        {secondsLeft > 0 ? (
          <>em {secondsLeft} segundos</>
        ) : (
          <button
            type="button"
            className="text-primary font-bold"
            onClick={handleResendToken}
          >
            clicando aqui
          </button>
        )}
      </div>

      <div className="mt-6 text-center text-sm font-sans text-gray-800 dark:text-gray-100">
        <span className="mr-1">Desistiu?</span>
        <button
          type="button"
          className="text-primary underline underline-offset-4"
          onClick={goToSignIn}
        >
          Voltar para o login
        </button>
      </div>
    </div>
  )
}
